import { useEffect } from "react";
import { useSidebar } from "./sidebar-context";
import { ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { CreditUsageBar } from "@/components/common/CreditUsageBar";
import {
  buildAppNavigation,
  canSeeFinancialNavigation,
  type NavigationItem,
} from "@/components/navigation/app-navigation";
import { useAuth, usePermission } from "@/hooks";
import { canOpenList, canSeeInbox } from "@/lib/clinic-grants";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui";

function routePath(url?: string) {
  return url?.split("?")[0] ?? "";
}

export function AppSidebar() {
  const { user } = useAuth();
  const { hasPermission, checkPermission } = usePermission();
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  const roleNames = user?.roles?.map((role) => role.name) ?? [];
  // SEC-GATES-02: list destinations follow the permission their list endpoint
  // checks, read from the user's real grants.
  const canOpen = (list: Parameters<typeof canOpenList>[1]) => canOpenList(checkPermission, list);

  const sections = buildAppNavigation({
    canViewFinancial: canSeeFinancialNavigation(roleNames),
    canViewFinanceAccounts: canOpen("financeAccounts"),
    canViewBudgets: canOpen("budgets"),
    canViewCategories: canOpen("categories"),
    canViewInbox: canSeeInbox(roleNames),
    canViewReports: hasPermission("report"),
    canViewPatients: canOpen("patients"),
    canViewSchedulings: canOpen("schedulings"),
    canViewUsers: checkPermission("user", "view user"),
    canViewPositions: hasPermission("positions"),
    canViewAgreements: hasPermission("convenants"),
    canViewEmployees: canOpen("employees"),
    canViewProcedures: canOpen("procedures"),
    canViewProviders: canOpen("providers"),
    canViewModels: hasPermission("models"),
    canViewModelTypes: hasPermission("modelstypes"),
    canViewRoles: checkPermission("role", "view role"),
    isSystemAdmin:
      user?.roles?.some((role) =>
        ["super_admin", "master"].includes(role.name)
      ) ?? false,
    isClinicAdmin:
      user?.roles?.some((role) => role.name === "admin") ?? false,
  });

  const isActive = (item: NavigationItem): boolean => {
    const path = routePath(item.url);
    if (path === "/settings/clinic" && location.pathname.startsWith("/my-business")) return true;
    if (["/permissions", "/registrations/users"].includes(path ?? "") && ["/permissions", "/registrations/users"].includes(location.pathname)) return true;
    if (path === "/") return location.pathname === "/";
    if (path && location.pathname.startsWith(path)) return true;
    return item.children?.some(isActive) ?? false;
  };

  const itemClasses = (item: NavigationItem) =>
    isActive(item)
      ? "bg-brand-soft text-brand"
      : "text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg-strong";

  const iconClasses = (item: NavigationItem) =>
    item.accent === "growth" && !isActive(item) ? "text-brand" : "";

  return (
    <Sidebar collapsible="icon" aria-label="Navegação principal">
      <SidebarHeader className="h-[72px] shrink-0 justify-center border-b border-app-border-subtle bg-app-surface px-4 group-data-[collapsible=icon]:px-1">
          <Link
            to="/"
            aria-label="CliniSharp — início"
            onClick={() => isMobile && setOpenMobile(false)}
            className="flex min-h-10 min-w-0 items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand group-data-[collapsible=icon]:justify-center"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--petrol))] shadow-sm">
              <img
                src="/media/logos/logo.png"
                width={24}
                height={24}
                alt=""
                className="object-contain brightness-0 invert"
              />
            </span>
            <span className="min-w-0 group-data-[collapsible=icon]:hidden">
              <span className="block truncate text-[15px] font-semibold tracking-tight text-app-fg-strong">
                CliniSharp
              </span>
              <span className="block truncate text-[11px] font-medium text-app-fg-subtle">
                Gestão da clínica
              </span>
            </span>
          </Link>
        </SidebarHeader>
      <SidebarContent className="bg-app-surface text-app-fg">
        <SidebarGroup className="px-2 py-3 group-data-[collapsible=icon]:px-1">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-4">
              {sections.map((section, index) => (
                <li
                  key={section.label}
                  // Collapsed, the groups lose their labels and seventeen icons
                  // read as one undifferentiated rail: a hairline and the
                  // group's initial keep the sections apart.
                  data-first-group={index === 0 ? "true" : undefined}
                  className="list-none group-data-[collapsible=icon]:border-t group-data-[collapsible=icon]:border-app-border-subtle group-data-[collapsible=icon]:pt-3 group-data-[collapsible=icon]:data-[first-group=true]:border-t-0 group-data-[collapsible=icon]:data-[first-group=true]:pt-0"
                >
                  <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-app-fg-subtle group-data-[collapsible=icon]:hidden">
                    {section.label}
                  </p>
                  <p className="mb-1.5 hidden text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-app-fg-subtle group-data-[collapsible=icon]:block">
                    <span aria-hidden>{section.label.charAt(0)}</span>
                    <span className="sr-only">{section.label}</span>
                  </p>
                  <ul className="space-y-0.5">
                    {section.items.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        {item.children ? (
                          <>
                            <div className="hidden group-data-[collapsible=icon]:block">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <SidebarMenuButton
                                    tooltip={item.title}
                                    aria-label={item.title}
                                    className={`justify-center rounded-xl transition-[background-color,color,box-shadow] duration-150 ${itemClasses(item)}`}
                                  >
                                    <item.icon className="size-4" />
                                  </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  side="right"
                                  align="start"
                                  className="min-w-60 rounded-xl border-app-border bg-app-surface p-1.5 shadow-premium-md"
                                >
                                  {item.children.map((child) => (
                                    <DropdownMenuItem key={child.title} asChild>
                                      <Link
                                        to={child.url ?? "#"}
                                        aria-current={isActive(child) ? "page" : undefined}
                                        onClick={() => isMobile && setOpenMobile(false)}
                                        className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-app-fg-muted focus:bg-brand-soft focus:text-brand"
                                      >
                                        <child.icon className="size-4" />
                                        {child.title}
                                      </Link>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>

                            <Collapsible defaultOpen={isActive(item)} className="group/collapsible group-data-[collapsible=icon]:hidden">
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton
                                  className={`w-full justify-between rounded-xl px-3 transition-[background-color,color,box-shadow] duration-150 ${itemClasses(item)}`}
                                >
                                  <span className="flex min-w-0 items-center gap-2.5">
                                    <item.icon className="size-4 shrink-0" />
                                    <span className="truncate text-[13px] font-medium">
                                      {item.title}
                                    </span>
                                  </span>
                                  <ChevronDown className="size-3.5 shrink-0 transition-transform duration-150 group-data-[state=open]/collapsible:rotate-180" />
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="ml-5 mt-1 space-y-0.5 border-l border-app-border-subtle pl-2">
                                {item.children.map((child) => (
                                  <SidebarMenuButton
                                    key={child.title}
                                    asChild
                                    className={`rounded-lg px-2.5 transition-colors duration-150 ${itemClasses(child)}`}
                                  >
                                    <Link
                                      to={child.url ?? "#"}
                                      aria-current={isActive(child) ? "page" : undefined}
                                      onClick={() => isMobile && setOpenMobile(false)}
                                      className="flex min-w-0 items-center gap-2"
                                    >
                                      <child.icon className="size-3.5 shrink-0" />
                                      <span className="truncate text-[12px]">
                                        {child.title}
                                      </span>
                                    </Link>
                                  </SidebarMenuButton>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          </>
                        ) : (
                          <SidebarMenuButton
                            asChild
                            tooltip={item.title}
                            className={`rounded-xl px-3 transition-[background-color,color,box-shadow] duration-150 group-data-[collapsible=icon]:justify-center ${itemClasses(item)}`}
                          >
                            <Link
                              to={item.url ?? "#"}
                              aria-label={item.title}
                              aria-current={isActive(item) ? "page" : undefined}
                              onClick={() => isMobile && setOpenMobile(false)}
                              className="flex min-w-0 items-center gap-2.5"
                            >
                              <item.icon
                                className={`size-4 shrink-0 ${iconClasses(item)}`}
                              />
                              <span className="truncate text-[13px] font-medium group-data-[collapsible=icon]:hidden">
                                {item.title}
                              </span>
                            </Link>
                          </SidebarMenuButton>
                        )}
                      </SidebarMenuItem>
                    ))}
                  </ul>
                </li>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-app-border-subtle p-0 group-data-[collapsible=icon]:hidden">
        <CreditUsageBar />
      </SidebarFooter>
    </Sidebar>
  );
}
